import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Table,
  Button,
  InputGroup,
  Spinner,
  Alert
} from "react-bootstrap";
import ReportsService from "../../../../services/reports-service";
import { IdentityContext } from "../../../../context/identity-context";
import RequestForReportModal from "./report-submission/components/modals/request-for-report-modal/request-for-report-modal";

const CertificationOfHoldingsReportModule = () => {
  const [bpIdFilter, setbpIdFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [reportsData, setReportsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const { user } = useContext(IdentityContext);
  const navigate = useNavigate();
  const reportService = new ReportsService();
  const sellingAgentEmailAddress = user.attributes['email'];

  // Fetch data on component mount using the ReportsService method with role "selling_agent_report"
  useEffect(() => {
    reportService.getrequestCertificationOfHoldings("selling_agent_report", sellingAgentEmailAddress)
      .then((data) => {
        setReportsData(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        setError(err);
        setLoading(false);
      });
  }, []);

  // Update filters to use the issuer as the bpId
  const filteredReports = reportsData.filter((report) => {
    const matchesBpId = bpIdFilter ? report.bpId.includes(bpIdFilter) : true;
    const matchesStatus = statusFilter ? report.status === statusFilter : true;
    const matchesSearch = searchQuery ? report.bpId.toLowerCase().includes(searchQuery.toLowerCase()) : true;
    return matchesBpId && matchesStatus && matchesSearch;
  });

  // Helper function to extract the date from the report title,
  // e.g. "Certification of Holdings (2025-02-04)" becomes "2025-02-04"
  const extractDate = (title) => {
    const dateMatch = title.match(/\(([^)]+)\)/);
    return dateMatch ? dateMatch[1] : "N/A";
  };

  // Preview function: opens the report file in a new window/tab
  const handlePreviewClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType,
        report.bpId,
        report.reportTitle,
        report.sellingAgentRequestDate
      );
      const newWindow = window.open(
        response.fileURL,
        "_blank",
        "noopener,noreferrer"
      );
      if (newWindow) newWindow.opener = null;
    } catch (error) {
      console.error("Error fetching report preview:", error.message);
    }
  };

  // Download function: downloads the report file using a temporary anchor element
  const handleDownloadClick = async (report) => {
    try {
      // Retrieve the report using the reportService
      const response = await reportService.retrieveReport(
        report.reportType,
        report.bpId,
        report.reportTitle,
        report.sellingAgentRequestDate
      );
      const url = response.fileURL;
      const filename = response.fileName || url.substring(url.lastIndexOf("/") + 1);

      const fileResponse = await fetch(url);
      if (!fileResponse.ok)
        throw new Error("Network response was not ok");
      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      // Create a temporary link to trigger the download
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      if (error.response) {
        console.error("Error fetching report download:", error.response.data);
        console.error("Error status:", error.response.status);
        console.error("Error headers:", error.response.headers);
      } else {
        console.error("Error message:", error.message);
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mt-3">
        <Col md={3}>
          <Form.Group controlId="filterBPID">
            <Form.Control
              as="select"
              value={bpIdFilter}
              onChange={(e) => setbpIdFilter(e.target.value)}
            >
              <option value="">BPID</option>
              {[...new Set(reportsData.map((report) => report.bpId))].map(
                (bpId) => (
                  <option key={bpId} value={bpId}>
                    {bpId}
                  </option>
                )
              )}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="filterStatus">
            <Form.Control
              as="select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Status</option>
              {[...new Set(reportsData.map((report) => report.status))].map(
                (status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                )
              )}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={{ span: 3, offset: 3 }}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ height: "auto" }}
            />
            <Button variant="primary">Search</Button>
          </InputGroup>
        </Col>
      </Row>
      
      {error && (
        <Alert variant="danger" className="mt-3">
          Error fetching reports: {error.message}
        </Alert>
      )}

      <Container className="report-container mb-3">
        <Row className="mb-2">
          <Col>
            <div className="d-flex justify-content-end">
              <Button variant="primary" onClick={() => setShowRequestModal(true)}>
                Request for Report
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th className="col-4">BPID</th>
                    <th className="col-4">Data as of</th>
                    <th className="col-2">Status</th>
                    <th className="col-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center">
                        <Spinner animation="border" role="status">
                          <span className="sr-only">Loading...</span>
                        </Spinner>
                      </td>
                    </tr>
                  ) : filteredReports.length > 0 ? (
                    filteredReports.map((report, index) => {
                      const isDisabled = report.status === "Not Available";
                      return (
                        <tr key={index}>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              <span>{report.bpId}</span>
                            </div>
                          </td>
                          <td>{report.sellingAgentRequestDate}</td>
                          <td>{report.status}</td>
                          <td>
                            <Button
                              variant="primary"className="action-button"
                              size="sm"
                              disabled={isDisabled}
                              onClick={() => handlePreviewClick(report)}
                            >
                              Preview
                            </Button>
                            <Button
                              variant="primary"
                              className="action-button"
                              size="sm"
                              disabled={isDisabled}
                              onClick={() => handleDownloadClick(report)}
                            >
                              Download
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">No Reports</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </Container>

      <RequestForReportModal
        show={showRequestModal}
        handleClose={() => setShowRequestModal(false)}
      />
    </Container>
  );
};

export default CertificationOfHoldingsReportModule;
